import { Experiment } from "@/http/api-types";

export type ExperimentLearningLoopClosure =
  | {
      isAvailable: true;
      closureSeconds: number;
      endToConclusionSeconds: number;
      conclusionToRolloutSeconds: number;
      concludedAt: string;
      rolloutAt: string;
    }
  | {
      isAvailable: false;
      reason: string;
    };

export function getExperimentLearningLoopClosure(experiment: Experiment): ExperimentLearningLoopClosure {
  const runtimeEndAt = parseValidDate(experiment.end_date);
  if (!runtimeEndAt) {
    return { isAvailable: false, reason: "No runtime end" };
  }

  const concludedAt = parseValidDate(experiment.concluded_at);
  const rolloutAt = parseValidDate(experiment.rollout_at);
  if (!concludedAt || !rolloutAt || !experiment.rollout_status) {
    return { isAvailable: false, reason: "Not closed" };
  }

  if (concludedAt.getTime() < runtimeEndAt.getTime()) {
    return { isAvailable: false, reason: "Not in baseline" };
  }

  if (rolloutAt.getTime() < concludedAt.getTime()) {
    return { isAvailable: false, reason: "Invalid history order" };
  }

  return {
    isAvailable: true,
    closureSeconds: secondsBetween(runtimeEndAt, rolloutAt),
    endToConclusionSeconds: secondsBetween(runtimeEndAt, concludedAt),
    conclusionToRolloutSeconds: secondsBetween(concludedAt, rolloutAt),
    concludedAt: concludedAt.toISOString(),
    rolloutAt: rolloutAt.toISOString(),
  };
}

export function formatLearningLoopDuration(seconds: number | undefined): string {
  if (seconds === undefined || Number.isNaN(seconds)) {
    return "-";
  }

  const totalSeconds = Math.max(0, Math.round(seconds));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function parseValidDate(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

function secondsBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / 1000;
}
